import React, { useState, useEffect, useMemo } from "react";
import { useSeller } from "../contexts/SellerContext";
import PageHeader from "../components/PageHeader";
import productData, {
  categoriesBySegment as defaultCategoriesBySegment,
  resolveSegmentKey,
} from "./SellerSegment";
import { useLotManagement } from "../hooks/useLotManagement";
import ImageUpload from "../components/upload-product/ImageUpload";
import LotDetailsDrawer from "../components/upload-product/LotDetailsDrawer";
import ProductForm from "../components/upload-product/ProductForm";
import ProductDetailsSection from "../components/upload-product/ProductDetailsSection";
import { db } from "../services/firebase";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import JsonProductBrowser from "../components/upload-product/JsonProductBrowser";
import "./UploadProduct.css";

const UploadProduct = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;

  const [activeTab, setActiveTab] = useState("ready");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [registeredProducts, setRegisteredProducts] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRegisteredProductId, setSelectedRegisteredProductId] =
    useState("");
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);
  const [jsonAttributes, setJsonAttributes] = useState({
    shape: "",
    color: "",
    luster: "",
    finishType: "",
    specification: "",
    inside: "",
    finishing: "",
    customization: [],
  });

  const handleJsonAttributeChange = (field, value) => {
    setJsonAttributes((prev) => ({ ...prev, [field]: value }));
    if (field === "specification") {
      setFormData((prev) => ({ ...prev, specification: value }));
    } else if (field === "finishing") {
      setFormData((prev) => ({
        ...prev,
        finishing: value,
        specification: prev.specification || value,
      }));
    } else if (field === "customization") {
      setFormData((prev) => ({
        ...prev,
        customization: Array.isArray(value) ? value.join(", ") : value,
      }));
    } else if (field === "finishType") {
      setFormData((prev) => {
        const next = {
          ...prev,
          styleType: value || prev.styleType || "Regular",
        };
        if (currentSegmentUpper === "MACHINE") {
          const ft = selectedJsonProduct?.details?.finishTypes;
          if (ft && typeof ft === "object" && !Array.isArray(ft) && ft[value]) {
            const defaultPower = ft[value][0] || "";
            setJsonAttributes((attr) => ({
              ...attr,
              specification: defaultPower,
            }));
            next.specification = defaultPower;
          }
        } else if (currentSegmentUpper === "DECOR") {
          next.specification = value || prev.specification || "PLANE";
        }
        return next;
      });
    } else if (field === "color") {
      setFormData((prev) => ({ ...prev, color: value }));
    } else if (field === "shape") {
      setFormData((prev) => ({ ...prev, shape: value }));
    } else if (field === "luster") {
      setFormData((prev) => ({ ...prev, luster: value }));
    } else if (field === "inside") {
      setFormData((prev) => ({ ...prev, inside: value }));
    }
  };

  // Non-jewellery: browse products straight from SellerSegment
  const JEWELRY_SEGMENTS = ["GOLD", "SILVER", "DIAMOND", "PLATINUM", "BANDHEL"];
  const currentSegmentUpper = (seller?.segment || "").toUpperCase();
  const isJewelrySegment = JEWELRY_SEGMENTS.includes(currentSegmentUpper);
  const isDecorSegment = [
    "DECOR",
    "BRAND",
    "TOOL",
    "TOOLS",
    "MACHINE",
  ].includes(currentSegmentUpper);
  const isGemSegment =
    currentSegmentUpper === "GEMS" || currentSegmentUpper === "PEARL";

  const [showJsonBrowser] = useState(!isJewelrySegment);
  const [showProductForm] = useState(isJewelrySegment);

  const [jsonCategory, setJsonCategory] = useState("");
  const [jsonSource, setJsonSource] = useState("");
  const [selectedJsonProduct, setSelectedJsonProduct] = useState(null);

  const [formData, setFormData] = useState({
    segment: seller.segment,
    category: "",
    productName: "",
    designCode: "",
    moqGram: "",
    moqSet: "",
    netWtPurity: "",
    wastage: "",
    specificationMC: "",
    specificationGramRate: "",
    setMc: "",
    netGramMc: "",
    paymentMethod: "RTGS",
    netWt: "",
    grossWt: "",
    specificationWt: "",
    instockGram: "",
    instockSet: "",
    styleType: "",
    specification: "",
    productSource: "",
    status: "pending",
    shape: "",
    color: "",
    luster: "",
  });

  const {
    Lot,
    lotSizes,
    setLotSizes,
    drawerLotSizes,
    setDrawerLotSizes,
    fieldErrors,
    setFieldErrors,
    toggleHandleLot,
    calculateSizeAverages,
    calculateTotals,
    calculateItemAverages,
    updateLotSizesWithAverages,
    openLotDrawer,
    closeLotDrawer,
    lotDrawerOpen,
    sizeRowRefs,
  } = useLotManagement(activeTab, formData, setFormData);

  const sizeAverages = useMemo(
    () => calculateSizeAverages(),
    [calculateSizeAverages],
  );
  const totals = useMemo(() => calculateTotals(), [calculateTotals]);

  const categoriesBySegment = useMemo(() => {
    return defaultCategoriesBySegment || {};
  }, []);

  const [registeredCombinations, setRegisteredCombinations] = useState({
    segments: new Set(),
    categories: new Set(),
    productNames: new Set(),
    sources: new Set(),
    styleTypes: new Set(),
    specifications: new Set(),
    combinations: new Set(),
  });

  const isRegistered = (type, value) => {
    if (!registeredCombinations[type]) return true;
    return registeredCombinations[type].has(value);
  };

  const showAlertMessage = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => setShowAlert(false), 5000);
  };

  const registeredProductList = useMemo(() => {
    const list = [];
    const seen = new Set();
    const currentSegment = (
      formData.segment ||
      seller?.segment ||
      ""
    ).toUpperCase();

    registeredProducts.forEach((reg) => {
      const regProducts = reg.products || {};
      const entries = Array.isArray(regProducts)
        ? regProducts.map((p) => [p.productName || p.name || "Product", p])
        : Object.entries(regProducts);

      entries.forEach(([name, details]) => {
        if (!details) return;
        const seg = (
          details.segment ||
          reg.segment ||
          currentSegment ||
          ""
        ).toUpperCase();
        const cat = details.category || reg.category || "";
        const src = details.productSource || reg.productSource || "";
        const style =
          details.selectedStyleType || details.styleType || "Regular";
        const spec = details.specification || "PLANE";
        const uid = `${
          reg.registrationId || ""
        }_${seg}_${cat}_${src}_${name}_${style}_${spec}`;

        if (!seen.has(uid)) {
          seen.add(uid);
          list.push({
            id: uid,
            registrationId: reg.registrationId,
            productName: name,
            segment: seg,
            category: cat,
            productSource: src,
            styleType: style,
            specification: spec,
            purity: details.purity || "",
            wastage: details.wastage || "",
            setMC: details.setMC || details.setMc || "",
            netGramMC: details.netGramMC || details.netGramMc || "",
            specificationMC: details.specificationMC || "",
            specificationGramRate: details.specificationGramRate || "",
            image: details.image || "",
            grossWt: details.grossWt || "",
            netWt: details.netWt || "",
            set: details.set || "1",
            rawDetails: details,
            reg,
          });
        }
      });
    });

    return list;
  }, [registeredProducts, seller?.segment, formData.segment]);

  const isCombinationRegistered = () => {
    // Jewellery segments use registered products; non-jewellery uses JSON selection
    if (isJewelrySegment) {
      return Boolean(selectedRegisteredProductId);
    }
    // Non-jewellery: requires a JSON product to be selected
    return Boolean(selectedJsonProduct);
  };

  // Lot/size data validity: segment-aware validation
  const isLotValid = useMemo(() => {
    if (!lotSizes || lotSizes.length === 0) return false;
    return lotSizes.every((item) => {
      const size = (item.size || "").trim();
      const set = parseFloat(item.set);
      const price = parseFloat(item.price);
      const caratRate = parseFloat(item.caratRate);
      const grossWt = parseFloat(
        activeTab === "order" ? item.avgGrossWt : item.grossWt,
      );
      const netWt = parseFloat(
        activeTab === "order" ? item.avgNetWt : item.netWt,
      );

      const baseValid = size !== "" && !isNaN(set) && set > 0;

      if (isDecorSegment) {
        return baseValid && !isNaN(price) && price > 0;
      }

      if (isGemSegment) {
        return (
          baseValid &&
          !isNaN(grossWt) &&
          grossWt > 0 &&
          !isNaN(netWt) &&
          netWt > 0 &&
          !isNaN(caratRate) &&
          caratRate > 0
        );
      }

      return (
        baseValid &&
        !isNaN(grossWt) &&
        grossWt > 0 &&
        !isNaN(netWt) &&
        netWt > 0
      );
    });
  }, [lotSizes, activeTab, isDecorSegment, isGemSegment]);

  const handleSelectRegisteredProduct = (id) => {
    setSelectedRegisteredProductId(id);
    if (!id) {
      setSelectedProduct(null);
      setFormData((prev) => ({
        ...prev,
        category: "",
        productName: "",
        styleType: "",
        specification: "",
        productSource: "",
        netWtPurity: "",
        wastage: "",
        setMc: "",
        netGramMc: "",
        specificationMC: "",
        specificationGramRate: "",
      }));
      setLotSizes([
        {
          size: "",
          set: "",
          grossWt: "",
          netWt: "",
          price: "",
          avgGrossWt: "",
          avgNetWt: "",
          avgSpecWt: "",
        },
      ]);
      return;
    }

    const selected = registeredProductList.find((p) => p.id === id);
    if (!selected) return;

    setSelectedProduct(selected.rawDetails);

    setFormData((prev) => ({
      ...prev,
      segment: selected.segment || seller.segment,
      category: selected.category || "",
      productName: selected.productName || "",
      productSource: selected.productSource || "",
      styleType: selected.styleType || "",
      specification: selected.specification || "",
      netWtPurity: selected.purity || "",
      wastage: selected.wastage || "",
      setMc: selected.setMC || "",
      netGramMc: selected.netGramMC || "",
      specificationMC: selected.specificationMC || "",
      specificationGramRate: selected.specificationGramRate || "",
    }));

    const details = selected.rawDetails || {};
    const initRow = {
      size: "",
      set: details.set || "1",
      grossWt: details.grossWt || "",
      netWt: details.netWt || "",
      ...calculateItemAverages({
        set: details.set || "1",
        grossWt: details.grossWt || "0",
        netWt: details.netWt || "0",
      }),
    };
    setLotSizes([initRow]);
  };

  const handleInputChange = (field, value) => {
    if (field === "moqSet") {
      setFormData((prev) => ({ ...prev, [field]: value }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    const fieldTypeMap = {
      segment: "segments",
      category: "categories",
      productName: "productNames",
      productSource: "sources",
      styleType: "styleTypes",
      specification: "specifications",
    };
    const fieldType = fieldTypeMap[field];
    if (fieldType && value && !isRegistered(fieldType, value)) {
      showAlertMessage(
        `This ${field} is not registered. Please register this product first.`,
      );
    }
  };

  useEffect(() => {
    if (!sellerId) return;
    setLoadingRegistrations(true);
    const fetchRegisteredProducts = async () => {
      try {
        const docSnap = await getDoc(doc(db, "ProductRegistrations", sellerId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          let allRegistrations = [];
          if (Array.isArray(data.registrations))
            allRegistrations = data.registrations;
          else if (data.registrations && typeof data.registrations === "object")
            allRegistrations = Object.values(data.registrations);
          else {
            const direct = Object.values(data).filter(
              (item) =>
                item &&
                typeof item === "object" &&
                (item.registrationId ||
                  item.products ||
                  item.approved !== undefined ||
                  item.status !== undefined),
            );
            if (direct.length > 0) allRegistrations = direct;
          }
          const approvedProducts = allRegistrations.filter(
            (r) =>
              (r.approved === true ||
                r.approved === "true" ||
                r.status === "approved" ||
                r.status === "Approved") &&
              r.products,
          );
          setRegisteredProducts(approvedProducts);
          const combinations = {
            segments: new Set(),
            categories: new Set(),
            productNames: new Set(),
            sources: new Set(),
            styleTypes: new Set(),
            specifications: new Set(),
            combinations: new Set(),
          };
          approvedProducts.forEach((reg) => {
            const regProducts = reg.products || {};
            const entries = Array.isArray(regProducts)
              ? regProducts.map((p) => [p.productName || p.name || "", p])
              : Object.entries(regProducts);

            entries.forEach(([name, details]) => {
              if (!details) return;
              const seg = details.segment || reg.segment;
              const cat = details.category || reg.category;
              const src = details.productSource || reg.productSource;
              const style = details.selectedStyleType || details.styleType;
              const spec = details.specification;

              if (seg) combinations.segments.add(seg);
              if (cat) combinations.categories.add(cat);
              if (src) combinations.sources.add(src);
              if (name) combinations.productNames.add(name);
              if (style) combinations.styleTypes.add(style);
              if (spec) combinations.specifications.add(spec);
              combinations.combinations.add(
                `${seg || ""}-${cat || ""}-${name}-${style || ""}-${
                  spec || ""
                }-${src || ""}`,
              );
            });
          });
          setRegisteredCombinations(combinations);
        }
      } catch (error) {
        console.error("Error fetching registered products:", error);
      } finally {
        setLoadingRegistrations(false);
      }
    };
    fetchRegisteredProducts();
  }, [sellerId]);

  // Sync selectedRegisteredProductId when form fields match a registered product
  useEffect(() => {
    if (!formData.category || !formData.productName) return;

    const matchedProd = registeredProductList.find(
      (p) =>
        (!p.segment ||
          !seller.segment ||
          p.segment.toUpperCase() === seller.segment.toUpperCase()) &&
        p.category === formData.category &&
        p.productName?.toLowerCase() === formData.productName?.toLowerCase() &&
        (!formData.styleType || p.styleType === formData.styleType) &&
        (!formData.specification ||
          p.specification === formData.specification) &&
        (!formData.productSource || p.productSource === formData.productSource),
    );

    if (matchedProd) {
      if (selectedRegisteredProductId !== matchedProd.id) {
        setSelectedRegisteredProductId(matchedProd.id);
        setSelectedProduct(matchedProd.rawDetails);
        setFormData((prev) => ({
          ...prev,
          wastage: matchedProd.wastage || prev.wastage || "",
          setMc: matchedProd.setMC || prev.setMc || "",
          specificationMC:
            matchedProd.specificationMC || prev.specificationMC || "",
          specificationGramRate:
            matchedProd.specificationGramRate ||
            prev.specificationGramRate ||
            "",
          netGramMc: matchedProd.netGramMC || prev.netGramMc || "",
          netWtPurity: matchedProd.purity || prev.netWtPurity || "",
        }));
      }
    }
  }, [
    formData.category,
    formData.productName,
    formData.styleType,
    formData.specification,
    formData.productSource,
    registeredProductList,
    seller.segment,
    selectedRegisteredProductId,
  ]);

  // DEBUG: detailed lot validation logging
  // useEffect(() => {
  //   console.log('=== LOT VALIDATION DEBUG ===');
  //   console.log('segment:', currentSegmentUpper, 'isDecor:', isDecorSegment, 'isGem:', isGemSegment);
  //   console.log('lotSizes:', JSON.stringify(lotSizes, null, 2));
  //   lotSizes.forEach((item, i) => {
  //     const size = (item.size || '').trim();
  //     const set = parseFloat(item.set);
  //     const price = parseFloat(item.price);
  //     const caratRate = parseFloat(item.caratRate);
  //     const grossWt = parseFloat(activeTab === 'order' ? item.avgGrossWt : item.grossWt);
  //     const netWt = parseFloat(activeTab === 'order' ? item.avgNetWt : item.netWt);
  //     const issues = [];
  //     if (size === '') issues.push('size empty');
  //     if (isNaN(set) || set <= 0) issues.push('set invalid: ' + item.set);
  //     if (isDecorSegment && (isNaN(price) || price <= 0)) issues.push('price invalid: ' + item.price);
  //     if (isGemSegment) {
  //       if (isNaN(grossWt) || grossWt <= 0) issues.push('grossWt invalid: ' + item.grossWt);
  //       if (isNaN(netWt) || netWt <= 0) issues.push('netWt invalid: ' + item.netWt);
  //       if (isNaN(price) || price <= 0) issues.push('price invalid: ' + item.price);
  //       if (isNaN(caratRate) || caratRate <= 0) issues.push('caratRate invalid: ' + item.caratRate);
  //     } else if (!isDecorSegment) {
  //       if (isNaN(grossWt) || grossWt <= 0) issues.push('grossWt invalid: ' + item.grossWt);
  //       if (isNaN(netWt) || netWt <= 0) issues.push('netWt invalid: ' + item.netWt);
  //     }
  //     console.log(`Row ${i}:`, issues.length ? issues : 'OK');
  //   });
  // }, [lotSizes, activeTab, isDecorSegment, isGemSegment,currentSegmentUpper]);

  const handleSizeChange = (index, field, value) => {
    if (field === "size") {
      const dupIdx = drawerLotSizes.findIndex(
        (item, i) => i !== index && item.size === value,
      );
      if (dupIdx !== -1) {
        if (sizeRowRefs.current[dupIdx]) {
          sizeRowRefs.current[dupIdx].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          const el = sizeRowRefs.current[dupIdx];
          el.classList.add("duplicate-highlight");
          setTimeout(() => el.classList.remove("duplicate-highlight"), 2000);
        }
        const sel = document.querySelector(
          `[data-row-index="${dupIdx}"] select`,
        );
        if (sel) sel.focus();
        return;
      }
    }
    const newSizes = [...drawerLotSizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    if (activeTab === "ready") {
      if (field === "grossWt" || field === "netWt") {
        const gross =
          parseFloat(field === "grossWt" ? value : newSizes[index].grossWt) ||
          0;
        const net =
          parseFloat(field === "netWt" ? value : newSizes[index].netWt) || 0;
        if (net > gross)
          setFieldErrors((prev) => ({
            ...prev,
            [`${index}-netWt`]: "Net Wt cannot be greater than Gross Wt",
          }));
        else {
          setFieldErrors((prev) => {
            const n = { ...prev };
            delete n[`${index}-netWt`];
            return n;
          });
        }
      }
      if (field === "set") {
        const sv = parseFloat(value) || 0;
        if (sv <= formData.moqSet)
          setFieldErrors((prev) => ({
            ...prev,
            [`${index}-set`]: "Set must be >= MOQ",
          }));
        else {
          setFieldErrors((prev) => {
            const n = { ...prev };
            delete n[`${index}-set`];
            return n;
          });
        }
      }
    }
    if (activeTab === "order") {
      if (field === "avgGrossWt" || field === "avgNetWt") {
        const aG =
          parseFloat(
            field === "avgGrossWt" ? value : newSizes[index].avgGrossWt,
          ) || 0;
        const aN =
          parseFloat(field === "avgNetWt" ? value : newSizes[index].avgNetWt) ||
          0;
        if (aN > aG)
          setFieldErrors((prev) => ({
            ...prev,
            [`${index}-avgNetWt`]: "Net Wt/Set cannot be > Gross Wt/Set",
          }));
        else {
          setFieldErrors((prev) => {
            const n = { ...prev };
            delete n[`${index}-avgNetWt`];
            return n;
          });
        }
      }
      if (field === "set") {
        const sv = parseFloat(value) || 0;
        if (sv <= formData.moqSet)
          setFieldErrors((prev) => ({
            ...prev,
            [`${index}-set`]: "Set must be >= MOQ",
          }));
        else {
          setFieldErrors((prev) => {
            const n = { ...prev };
            delete n[`${index}-set`];
            return n;
          });
        }
      }
    }
    if (
      activeTab === "ready" &&
      (field === "set" || field === "grossWt" || field === "netWt")
    ) {
      const avgs = calculateItemAverages(newSizes[index]);
      newSizes[index] = {
        ...newSizes[index],
        avgGrossWt: avgs.avgGrossWt,
        avgNetWt: avgs.avgNetWt,
        avgSpecWt: avgs.avgSpecWt,
      };
    }
    if (
      activeTab === "order" &&
      (field === "set" || field === "avgGrossWt" || field === "avgNetWt")
    ) {
      newSizes[index] = {
        ...newSizes[index],
        grossWt: "0",
        netWt: "0",
        avgSpecWt: (
          parseFloat(newSizes[index].avgGrossWt || 0) -
          parseFloat(newSizes[index].avgNetWt || 0)
        ).toFixed(3),
      };
    }
    setDrawerLotSizes(newSizes);
  };

  const handleSaveLot = () => {
    if (!validateSaveData(drawerLotSizes)) return;
    setLotSizes(drawerLotSizes);
    const t = calculateTotals();
    const updated = updateLotSizesWithAverages(drawerLotSizes);
    setLotSizes(updated);
    setFormData((prev) => ({
      ...prev,
      grossWt: t.totalGrossWt.toFixed(3),
      netWt: t.totalNetWt.toFixed(3),
      instockGram: activeTab === "ready" ? t.totalGrossWt.toFixed(3) : "",
      instockSet: activeTab === "ready" ? t.totalSets.toString() : "",
      moqSet: Lot ? prev.moqSet || t.totalSets.toString() : prev.moqSet,
      lotSizes: updated,
      sizes: updated.map((item) => item.size).filter(Boolean),
    }));
    closeLotDrawer();
  };

  const handleOpenLotDrawer = () => {
    const parsedMOQ = parseMOQ(formData.moqSet);
    if (!formData.moqSet || parsedMOQ <= 0) {
      showAlertMessage(
        "Please enter MOQ Set first before editing size details.",
      );
      return;
    }
    openLotDrawer();
  };

  const handleAddLotSize = () => {
    if (!Lot || drawerLotSizes.length >= getSizeOptions().length) return;
    const available = getSizeOptions().filter(
      (s) => !drawerLotSizes.some((item) => item.size === s),
    );
    if (available.length === 0) {
      alert("All available sizes have been added.");
      return;
    }
    setDrawerLotSizes([
      ...drawerLotSizes,
      {
        size: "",
        set: "",
        grossWt: "",
        netWt: "",
        price: "",
        avgGrossWt: "",
        avgNetWt: "",
        avgSpecWt: "",
      },
    ]);
  };

  const validateSaveData = (lotData) => {
    const parsedMOQ = parseMOQ(formData.moqSet);
    if (activeTab === "ready") {
      if (parsedMOQ <= 0) {
        showAlertMessage("MOQ must be greater than 0");
        return false;
      }
      if (lotData.length > 0) {
        const inv = lotData.filter((item) => parseFloat(item.set) < parsedMOQ);
        if (inv.length > 0) {
          showAlertMessage(
            `MOQ ${parsedMOQ} not met in sizes: ${inv
              .map((s) => s.size || "Unnamed")
              .join(", ")}`,
          );
          return false;
        }
      }
      if (lotData.length > 0) {
        const invW = lotData.filter((item) => {
          const g = parseFloat(item.grossWt) || 0,
            n = parseFloat(item.netWt) || 0;
          return !isNaN(g) && !isNaN(n) && n > g;
        });
        if (invW.length > 0) {
          showAlertMessage(
            `Net Wt. cannot be > Gross Wt. in sizes: ${invW
              .map((s) => s.size || "Unnamed")
              .join(", ")}`,
          );
          return false;
        }
      }
      const t = calculateTotals(lotData);
      if (parsedMOQ > 0 && t.totalSets > 0 && parsedMOQ > t.totalSets) {
        showAlertMessage(`MOQ Set cannot be > total sets (${t.totalSets}).`);
        return false;
      }
    } else {
      const hasCond = lotData.some((item) => {
        const aG = parseFloat(item.avgGrossWt) || 0,
          aN = parseFloat(item.avgNetWt) || 0;
        return aG >= aN;
      });
      if (hasCond && parsedMOQ <= 0) {
        showAlertMessage(
          "Gross wt / Set >= Net wt / Set - Please enter MOQ Set",
        );
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const handler = (event) => {
      if (isSizeDropdownOpen && !event.target.closest(".multi-select-dropdown"))
        setIsSizeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  });

  const handleSubmit = async () => {
    if (
      !seller.segment.toUpperCase() ||
      !formData.category ||
      !formData.productName ||
      !formData.styleType ||
      !formData.specification ||
      !formData.productSource
    ) {
      showAlertMessage("Please fill all required product information fields.");
      return;
    }
    if (!isCombinationRegistered()) {
      showAlertMessage(
        "This product combination is not registered. Please register this product first before uploading.",
      );
      return;
    }
    if (!uploadedImages.length) {
      showAlertMessage("Please upload at least one product image.");
      return;
    }
    if (!validateSaveData(drawerLotSizes)) return;
    try {
      const finalLotSizes = updateLotSizesWithAverages(lotSizes);
      const productDataToSave = {
        sellerId,
        segment: seller.segment.toUpperCase(),
        category: formData.category,
        productName: formData.productName,
        designCode: formData.designCode?.trim() || "",
        styleType: formData.styleType,
        specification: formData.specification,
        productSource: formData.productSource,
        Lot,
        specificationMC: formData.specificationMC,
        specificationGramRate: formData.specificationGramRate,
        images: uploadedImages,
        serviceType: activeTab,
        wastage: formData.wastage,
        setMc: formData.setMc,
        netGramMc: formData.netGramMc,
        purity: formData.netWtPurity,
        moqSet: parseMOQ(formData.moqSet),
        lotDetails: finalLotSizes,
        sellerPayment: formData.paymentMethod,
        paymentMethod: formData.paymentMethod,
        status: "pending",
        timestamp: new Date(),
        shape: jsonAttributes.shape || "",
        color: jsonAttributes.color || "",
        luster: jsonAttributes.luster || "",
        finishType: jsonAttributes.finishType || "",
        finishing: jsonAttributes.finishing || formData.finishing || "",
        inside: jsonAttributes.inside || "",
        customization: Array.isArray(jsonAttributes.customization)
          ? jsonAttributes.customization.join(", ")
          : jsonAttributes.customization || formData.customization || "",
      };
      await addDoc(collection(db, "products"), productDataToSave);
      alert("Product added successfully!");
      setUploadedImages([]);
      setSelectedProduct(null);
      setSelectedRegisteredProductId("");
      setLotSizes([
        {
          size: "",
          set: "",
          grossWt: "",
          netWt: "",
          price: "",
          avgGrossWt: "",
          avgNetWt: "",
          avgSpecWt: "",
        },
      ]);
      setFormData({
        segment: seller.segment.toUpperCase(),
        category: "",
        productName: "",
        designCode: "",
        moqGram: "",
        moqSet: "",
        netWtPurity: "",
        wastage: "",
        setMc: "",
        netGramMc: "",
        paymentMethod: "RTGS",
        netWt: "",
        grossWt: "",
        specificationWt: "",
        instockGram: "",
        instockSet: "",
        styleType: "",
        specification: "",
        productSource: "",
        status: "pending",
        shape: "",
        color: "",
        luster: "",
      });
      setJsonAttributes({ shape: "", color: "", luster: "", finishType: "" });
      setIsSizeDropdownOpen(false);
    } catch (error) {
      console.error("Error saving product:", error);
      alert("Error saving product. Please try again.");
    }
  };

  const parseMOQ = (mq) => {
    if (typeof mq !== "string") return parseFloat(mq) || 0;
    const numParts = mq.match(/(\d+)(?:\*(\d+))?|(\d+)(?:PC)?|Min\s+(\d+)/);
    if (numParts) {
      const [, base, multiplier, pc, min] = numParts;
      if (base)
        return parseFloat(base) * (multiplier ? parseFloat(multiplier) : 1);
      if (pc) return parseFloat(pc);
      if (min) return parseFloat(min);
    }
    return 0;
  };

  const getSizeInfo = () => {
    const rawSeg = formData.segment || seller.segment || "";
    const segment = (resolveSegmentKey(rawSeg) || rawSeg).toUpperCase();
    const category = formData.category;
    const source = formData.productSource;
    const productName = formData.productName;
    if (!segment || !category || !source || !productName) return null;
    const segObj = productData[segment] || productData[rawSeg.toUpperCase()];
    if (!segObj) return null;

    if (segObj[category]?.[source]?.[productName.toUpperCase()]) {
      return segObj[category][source][productName.toUpperCase()];
    }
    if (segObj[category]?.[source]?.[productName]) {
      return segObj[category][source][productName];
    }
    const catObj =
      segObj[category] ||
      Object.entries(segObj).find(
        ([k]) => k.toUpperCase() === category.toUpperCase(),
      )?.[1];
    if (!catObj) return null;
    const srcObj =
      catObj[source] ||
      Object.entries(catObj).find(
        ([k]) => k.toUpperCase() === source.toUpperCase(),
      )?.[1];
    if (!srcObj) return null;
    return (
      srcObj[productName] ||
      Object.entries(srcObj).find(
        ([k]) => k.toUpperCase() === productName.toUpperCase(),
      )?.[1] ||
      null
    );
  };

  const getSizeOptions = () =>
    getSizeInfo()?.options || getSizeInfo()?.dimensions || [];
  const getSizeUnit = () => getSizeInfo()?.sizing_unit || "";
  const getCategoriesForSegment = () => {
    if (!seller.segment) return [];
    const seg = (
      resolveSegmentKey(seller.segment) || seller.segment
    ).toUpperCase();
    return (
      categoriesBySegment[seg] ||
      categoriesBySegment[seller.segment.toUpperCase()] ||
      []
    );
  };

  // SellerSegment is 4 levels deep: segment > category > source > product
  // e.g. PEARL > A > BASARA > LOOSE (each product carries sizing_unit / options / note / image)
  const isProductNode = (val) =>
    !!val &&
    typeof val === "object" &&
    !Array.isArray(val) &&
    ("sizing_unit" in val ||
      "options" in val ||
      "dimensions" in val ||
      "image" in val ||
      "note" in val);

  const resetJsonProductFields = (prev, extra = {}) => ({
    ...prev,
    productName: "",
    productSource: "",
    styleType: "",
    specification: "",
    shape: "",
    color: "",
    luster: "",
    finishType: "",
    ...extra,
  });

  // Level 1 = category (e.g. "A"), level 2 = source (e.g. "BASARA")
  const handleJsonCategorySelect = (cat) => {
    setJsonCategory(cat);
    setJsonSource("");
    setSelectedJsonProduct(null);
    setFormData((prev) => resetJsonProductFields(prev, { category: cat }));
  };

  const handleJsonSourceSelect = (src) => {
    setJsonSource(src);
    setSelectedJsonProduct(null);
    setFormData((prev) => resetJsonProductFields(prev, { productSource: src }));
  };

  const handleJsonProductSelect = (entry) => {
    if (!entry) return;
    setSelectedJsonProduct(entry);
    // Extract finishTypes options - handle both array and object formats
    const ft = entry.details?.finishTypes || entry.details?.finishes;
    let finishTypeOptions = [];
    if (Array.isArray(ft)) {
      finishTypeOptions = ft;
    } else if (ft && typeof ft === "object") {
      finishTypeOptions = Object.keys(ft);
    }
    const firstFinishType = finishTypeOptions[0] || "";
    const colorOpts = entry.details?.color || entry.details?.colors;
    const firstColor = Array.isArray(colorOpts)
      ? colorOpts[0]
      : colorOpts || "";

    const isMachine = currentSegmentUpper === "MACHINE";
    const isTool =
      currentSegmentUpper === "TOOL" || currentSegmentUpper === "TOOLS";
    const isBrand = currentSegmentUpper === "BRAND";

    let firstSpec = "";
    let firstFinishing = "";
    if (isMachine) {
      if (ft && typeof ft === "object" && !Array.isArray(ft)) {
        const powers = ft[firstFinishType] ||
          ft["LIGHT DUTY"] || ["1-2(kW)", "2-3(kW)", "3-4(kW)"];
        firstSpec = powers[0] || "1-2(kW)";
      } else {
        firstSpec = "1-2(kW)";
      }
    } else if (isTool) {
      const rawSpec = entry.details?.specification;
      const specs = Array.isArray(rawSpec)
        ? rawSpec.flat()
        : rawSpec
        ? [rawSpec]
        : ["Without grip", "PVC grip", "Rubber grip", "Plastic grip"];
      firstSpec = specs[0] || "Without grip";
    } else if (isBrand) {
      const cat = (entry.category || jsonCategory || "").toUpperCase();
      const rawSpec = entry.details?.specification;
      const specs = Array.isArray(rawSpec)
        ? rawSpec.flat()
        : rawSpec
        ? [rawSpec]
        : [];
      if (cat === "PRINTED") {
        const finishings = Array.isArray(entry.details?.finishing)
          ? entry.details.finishing
          : [];
        firstFinishing = finishings[0] || "";
        firstSpec = firstFinishing || specs[0] || "Standard";
      } else {
        firstSpec = specs[0] || "Standard";
      }
    } else if (isDecorSegment) {
      firstSpec = firstFinishType || "PLANE";
    } else {
      firstSpec = "PLANE";
    }

    setJsonAttributes({
      shape: Array.isArray(entry.details?.shape)
        ? entry.details?.shape[0]
        : entry.details?.shape || "",
      color: firstColor,
      luster: Array.isArray(entry.details?.luster)
        ? entry.details?.luster[0]
        : entry.details?.luster || "",
      finishType: firstFinishType,
      specification: firstSpec,
      finishing: firstFinishing,
      inside: Array.isArray(entry.details?.inside)
        ? entry.details?.inside[0]
        : entry.details?.inside || "",
      customization: [],
    });
    setFormData((prev) => ({
      ...prev,
      segment: currentSegmentUpper,
      category: entry.category || jsonCategory,
      productName: entry.name,
      productSource: entry.source || jsonSource || "",
      // Defaults required by handleSubmit (Regular / PLANE)
      styleType: firstFinishType || prev.styleType || "Regular",
      specification: firstSpec || prev.specification || "PLANE",
      finishing: firstFinishing,
      customization: "",
    }));
  };

  const clearJsonSelection = (keepCategory = false) => {
    setSelectedJsonProduct(null);
    setJsonAttributes({
      shape: "",
      color: "",
      luster: "",
      finishType: "",
      specification: "",
      inside: "",
      finishing: "",
      customization: [],
    });
    if (!keepCategory) {
      setJsonCategory("");
      setJsonSource("");
    }
    setFormData((prev) => ({
      ...prev,
      category: keepCategory ? prev.category : "",
      productSource: keepCategory ? prev.productSource : "",
      productName: "",
      styleType: "",
      specification: "",
    }));
  };

  // Non-jewellery JSON product browsing
  const jsonCategories = useMemo(() => {
    const seg = resolveSegmentKey(currentSegmentUpper) || currentSegmentUpper;
    return (
      categoriesBySegment[seg] || categoriesBySegment[currentSegmentUpper] || []
    );
  }, [currentSegmentUpper, categoriesBySegment]);

  const jsonSources = useMemo(() => {
    const seg = resolveSegmentKey(currentSegmentUpper) || currentSegmentUpper;
    const cat = jsonCategory;
    if (!seg || !cat) return [];
    const segData = productData[seg] || productData[currentSegmentUpper];
    if (!segData || typeof segData !== "object") return [];
    const catData =
      segData[cat] ||
      Object.entries(segData).find(
        ([k]) => k.toUpperCase() === cat.toUpperCase(),
      )?.[1];
    if (!catData || typeof catData !== "object") return [];
    return (
      Object.entries(catData)
        // Sources are plain nested objects; products are leaf objects (sizing_unit/options/...)
        .filter(
          ([, val]) =>
            typeof val === "object" && val !== null && !isProductNode(val),
        )
        .map(([key]) => key)
    );
  }, [currentSegmentUpper, jsonCategory]);

  // Always keep a valid source selected for the chosen category (e.g. PEARL > A > BASARA)
  useEffect(() => {
    if (!jsonCategory) {
      if (jsonSource) setJsonSource("");
      return;
    }
    const validSource = jsonSources.includes(jsonSource)
      ? jsonSource
      : jsonSources[0] || "";
    if (validSource !== jsonSource) {
      setJsonSource(validSource);
      setSelectedJsonProduct(null);
      setFormData((prev) =>
        resetJsonProductFields(prev, { productSource: validSource }),
      );
    }
  }, [jsonCategory, jsonSources, jsonSource]);

  const jsonProductEntries = useMemo(() => {
    const seg = resolveSegmentKey(currentSegmentUpper) || currentSegmentUpper;
    const cat = jsonCategory;
    if (!seg || !cat) return [];
    const segData = productData[seg] || productData[currentSegmentUpper];
    if (!segData || typeof segData !== "object") return [];
    const catData =
      segData[cat] ||
      Object.entries(segData).find(
        ([k]) => k.toUpperCase() === cat.toUpperCase(),
      )?.[1];
    if (!catData || typeof catData !== "object") return [];
    const sourceObj = jsonSource
      ? catData[jsonSource] ||
        Object.entries(catData).find(
          ([k]) => k.toUpperCase() === jsonSource.toUpperCase(),
        )?.[1]
      : null;
    // Fallback: a category that holds products directly (no source level)
    const productsObj =
      sourceObj && typeof sourceObj === "object" ? sourceObj : catData;
    return Object.entries(productsObj)
      .filter(([, details]) => isProductNode(details))
      .map(([name, details]) => ({
        name,
        details,
        image: details?.image || "",
        sizingUnit: details?.sizing_unit || "",
        options: details?.options || details?.dimensions || [],
        note: details?.note || "",
        shape: details?.shape || [],
        color: details?.color || details?.colors || [],
        luster: details?.luster || [],
        finishTypes: details?.finishTypes || details?.finishes || [],
        specification: details?.specification || [],
        finishing: details?.finishing || [],
        inside: details?.inside || [],
        customization: details?.customization || [],
        category: cat,
        source: jsonSource || "",
        productSources: jsonSource ? [jsonSource] : [],
      }));
  }, [currentSegmentUpper, jsonCategory, jsonSource]);

  return (
    <>
      <PageHeader title="Upload Product" />
      <div className="add-product-container">
        {showAlert && (
          <div className="alert-message">
            <div className="alert-content">
              <span className="alert-text">{alertMessage}</span>
              <button
                className="alert-close"
                onClick={() => setShowAlert(false)}
              >
                ×
              </button>
            </div>
          </div>
        )}
        <div className="radio-group">
          <label
            className={`radio-option ${activeTab === "ready" ? "active" : ""}`}
          >
            <input
              type="radio"
              value="ready"
              checked={activeTab === "ready"}
              onChange={(e) => setActiveTab(e.target.value)}
            />
            Ready Serve
          </label>
          <label
            className={`radio-option ${activeTab === "order" ? "active" : ""}`}
          >
            <input
              type="radio"
              value="order"
              checked={activeTab === "order"}
              onChange={(e) => setActiveTab(e.target.value)}
            />
            Order Serve
          </label>
        </div>

        {/* Show JSON product browser for non-jewellery segments */}
        {showJsonBrowser && (
          <JsonProductBrowser
            segment={currentSegmentUpper}
            categories={jsonCategories}
            sources={jsonSources}
            productEntries={jsonProductEntries}
            selectedCategory={jsonCategory}
            selectedSource={jsonSource}
            selectedProduct={selectedJsonProduct}
            onSelectCategory={handleJsonCategorySelect}
            onSelectSource={handleJsonSourceSelect}
            onSelectProduct={handleJsonProductSelect}
            onClearSelection={() => clearJsonSelection(true)}
            onClearAll={() => {
              setJsonCategory("");
              setJsonSource("");
              setSelectedJsonProduct(null);
              setJsonAttributes({
                shape: "",
                color: "",
                luster: "",
                finishType: "",
                specification: "",
                inside: "",
                finishing: "",
                customization: [],
              });
              setFormData((prev) => ({
                ...prev,
                category: "",
                productName: "",
                productSource: "",
                styleType: "",
                specification: "",
                finishing: "",
                customization: "",
                shape: "",
                color: "",
                luster: "",
                finishType: "",
              }));
            }}
            attributeValues={jsonAttributes}
            onAttributeChange={handleJsonAttributeChange}
          />
        )}
        <div className="content">
          {showProductForm && (
            <ProductForm
              seller={seller}
              formData={formData}
              handleInputChange={handleInputChange}
              getCategoriesForSegment={getCategoriesForSegment}
              isRegistered={isRegistered}
              registeredCombinations={registeredCombinations}
              registeredProductList={registeredProductList}
              selectedRegisteredProductId={selectedRegisteredProductId}
              handleSelectRegisteredProduct={handleSelectRegisteredProduct}
              loadingRegistrations={loadingRegistrations}
              selectedProduct={selectedProduct}
            />
          )}
          <ImageUpload
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            mainImageIndex={mainImageIndex}
            setMainImageIndex={setMainImageIndex}
            uploading={uploading}
            setUploading={setUploading}
          />
          <ProductDetailsSection
            formData={formData}
            Lot={Lot}
            activeTab={activeTab}
            handleInputChange={handleInputChange}
            toggleHandleLot={toggleHandleLot}
            openLotDrawer={handleOpenLotDrawer}
            getSizeUnit={getSizeUnit}
            getSizeInfo={getSizeInfo}
            lotSizes={lotSizes}
            totals={totals}
            segment={currentSegmentUpper}
          />
          <LotDetailsDrawer
            lotDrawerOpen={lotDrawerOpen}
            closeLotDrawer={closeLotDrawer}
            drawerLotSizes={drawerLotSizes}
            setDrawerLotSizes={setDrawerLotSizes}
            fieldErrors={fieldErrors}
            Lot={Lot}
            activeTab={activeTab}
            segment={currentSegmentUpper}
            handleSizeChange={handleSizeChange}
            handleAddLotSize={handleAddLotSize}
            handleSaveLot={handleSaveLot}
            getSizeOptions={getSizeOptions}
            sizeAverages={sizeAverages}
            totals={totals}
          />
          {/* <button onClick={() => console.log('uploading:', uploading, 'isCombinationRegistered:', isCombinationRegistered(), 'isLotValid:', isLotValid, 'images:', uploadedImages.length)}>
  Debug Log
</button> */}
          <button
            onClick={handleSubmit}
            disabled={!isCombinationRegistered() || !isLotValid}
            className="submit-button"
            title={
              !isCombinationRegistered()
                ? "Please select a registered product"
                : isLotValid
                ? isDecorSegment
                  ? "Please fill size, sets (> 0) and price before submitting"
                  : isGemSegment
                  ? "Please fill size, sets (> 0), gross wt, net wt, price and carat rate before submitting"
                  : "Please fill size, sets (> 0), gross weight and net weight before submitting"
                : uploadedImages.length === 0
                ? "Please upload at least one product image before submitting"
                : ""
            }
          >
            {uploading ? "Uploading..." : "QC Request"}
          </button>
        </div>
      </div>
    </>
  );
};

export default UploadProduct;
